import styled from 'styled-components';

import { colors } from 'constants/colors';
import { DetailLink } from 'components/DetailsView';

import {
    GetMicrostoreProductsQuery,
    GetSiteSetupQuery,
    MicrostoreColumnSetupFragmentFragment,
    useGetColumnProductQuery,
    useProductStockUpdatedSubscription,
    useAddPostSiteSetupZeroInventoryProductsMutation,
} from 'data/__generated__';
import { productStockUpdatedHandler } from '../../../cacheHandlers/productStockCacheHandler';
import { toast } from 'react-toastify';
import { confirmPopup } from 'components/Popups/Confirm';

export const LocationDetailsSection = ({
    columnSetup,
    selectedLocationId,
    products,
    site,
    siteSetupDate,
}: {
    columnSetup: MicrostoreColumnSetupFragmentFragment;
    selectedLocationId: string | null;
    products: GetMicrostoreProductsQuery['getMicrostoreProducts'];
    site: GetSiteSetupQuery['getSiteSetup']['site'];
    siteSetupDate: string;
}) => {
    const [addPostSiteSetupZeroInventoryProducts] = useAddPostSiteSetupZeroInventoryProductsMutation();

    const handleZeroInventoryClick = async (productId: string) => {
        const setupProduct = products.find((p) => p._id === productId);
        const productName = setupProduct?.name || 'ce produit';

        const hasConfirmed = await confirmPopup({
            title: 'Confirmer',
            message: `Êtes-vous sûr.e de vouloir indiquer qu'il n'y avait pas de produit "${productName}" avant le rangement du site ? Cette action mettra à jour le stock.`,
        });

        if (!hasConfirmed) {
            return;
        }

        const result = await addPostSiteSetupZeroInventoryProducts({
            variables: {
                microstoreColumnSetupId: columnSetup._id,
                productIds: [productId],
            },
        });

        if (result.data?.addPostSiteSetupZeroInventoryProducts.success) {
            toast.success('Produit mis à jour avec succès');
        } else {
            toast.error(result.data?.addPostSiteSetupZeroInventoryProducts.error || 'Erreur lors de la mise à jour');
        }
    };

    const isSiteSetupDay = () => {
        const today = new Date();
        const siteSetupDateObj = new Date(siteSetupDate);

        const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const siteSetupDateOnly = new Date(
            siteSetupDateObj.getFullYear(),
            siteSetupDateObj.getMonth(),
            siteSetupDateObj.getDate(),
        );

        return todayDateOnly.getTime() === siteSetupDateOnly.getTime();
    };

    const setupLocations = columnSetup.microstoreLocations;
    const setupLocation = setupLocations?.find((loc) => loc.microstoreLocationId === selectedLocationId);
    const setupProductId = setupLocation?.productId;
    const setupReplacementProductId = setupLocation?.replacementProductId;
    const setupProduct = products.find((p) => p._id === setupProductId);
    const setupReplacementProduct = products.find((p) => p._id === setupReplacementProductId);

    const { data: productStockData } = useGetColumnProductQuery({
        variables: { columnId: columnSetup.microstoreColumnId, productId: setupProductId ?? '' },
        skip: !setupProductId,
        fetchPolicy: 'network-only',
    });

    useProductStockUpdatedSubscription({
        variables: { siteId: site._id },
        skip: !setupProductId,
        onData({ data }) {
            if (!data?.data) return;
            productStockUpdatedHandler(data.data.productStockUpdated, columnSetup.microstoreColumnId);
        },
    });

    const productStock = productStockData?.columnProduct?.stock;
    const productVariations = productStockData?.columnProduct?.variations || [];

    return (
        <Container>
            <Title>Détails du produit</Title>
            <Content>
                <Section>
                    {setupProduct ? (
                        <DetailLink
                            label="Produit Avant"
                            name={setupProduct.name}
                            value={setupProduct._id}
                            path={`column/${columnSetup.microstoreColumnId}/stocks`}
                        />
                    ) : (
                        <>
                            <Label>Produit Avant:</Label>
                            <Value>Aucun produit</Value>
                        </>
                    )}
                    {productStock !== undefined && (
                        <>
                            <Label>Stock au site ({site.name}):</Label>
                            <Value>{productStock}</Value>
                            {selectedLocationId && setupProductId && productStock !== 0 && isSiteSetupDay() && (
                                <ButtonContainer>
                                    <ZeroInventoryButton
                                        $isSelected={
                                            columnSetup.postSiteSetupZeroInventoryProductIds?.includes(
                                                setupProductId,
                                            ) ?? false
                                        }
                                        onClick={() => handleZeroInventoryClick(setupProductId)}
                                    >
                                        Il n'y avait pas de produit avant le rangement du site
                                    </ZeroInventoryButton>
                                </ButtonContainer>
                            )}
                        </>
                    )}
                    {productVariations.length > 0 && (
                        <>
                            <Label>Historique des variations (10 dernières):</Label>
                            <VariationsList>
                                {productVariations.slice(0, 10).map((variation) => (
                                    <VariationItem key={variation._id}>
                                        <VariationDate>{new Date(variation.createdAt).toLocaleString()}</VariationDate>
                                        <VariationDelta $isPositive={variation.delta > 0}>
                                            {variation.delta}
                                        </VariationDelta>
                                        <VariationType>{variation.type}</VariationType>
                                    </VariationItem>
                                ))}
                            </VariationsList>
                        </>
                    )}
                </Section>
                <Section>
                    {setupReplacementProduct ? (
                        <DetailLink
                            label="Produit Après"
                            name={setupReplacementProduct.name}
                            value={setupReplacementProduct._id}
                            path={`column/${columnSetup.microstoreColumnId}/stocks`}
                        />
                    ) : (
                        <>
                            <Label>Produit Après:</Label>
                            <Value>Aucun produit</Value>
                        </>
                    )}
                </Section>
            </Content>
        </Container>
    );
};

const Container = styled.div`
    background-color: ${colors.pureWhite};
    border-radius: 5px;
    padding: 15px;
`;

const Title = styled.div`
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 15px;
    color: ${colors.pureBlack};
`;

const Content = styled.div``;

const Section = styled.div`
    margin-bottom: 15px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    align-items: center;
    margin-top: 10px;
`;

const ZeroInventoryButton = styled.button<{ $isSelected: boolean }>`
    display: inline-block;
    text-align: center;
    padding: 12px 24px;
    font-size: 15px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 2px;
    border-radius: 4px;
    border: 1px solid ${colors.greenDarker};
    cursor: pointer;

    ${({ $isSelected }) => {
        if ($isSelected) {
            return `
                background-color: ${colors.greenDarker};
                color: ${colors.black};
            `;
        }

        return `
            background-color: ${colors.green};
            color: ${colors.black};

            &:hover {
                background-color: ${colors.greenDarker};
            }
        `;
    }}
`;

const Label = styled.div`
    font-weight: bold;
    font-size: 14px;
    margin-bottom: 5px;
`;

const Value = styled.div<{ $color?: string }>`
    font-size: 14px;
    color: ${({ $color }) => $color || colors.pureBlack};
    margin-bottom: 5px;
`;

const VariationsList = styled.div`
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const VariationItem = styled.div`
    display: flex;
    align-items: center;
    font-size: 13px;
    padding: 4px 0;
`;

const VariationDate = styled.span`
    min-width: 120px;
`;

const VariationDelta = styled.span<{ $isPositive: boolean }>`
    font-weight: bold;
    color: ${({ $isPositive }) => ($isPositive ? colors.green : colors.red)};
    min-width: 40px;
    margin-left: 20px;
`;

const VariationType = styled.span`
    font-size: 12px;
`;
